import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, CalendarDays, Wallet, Check, X, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api } from "../../lib/api";

const ReqCard = ({ it, onDone }) => {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التنفيذ"); } finally { setBusy(false); }
  };
  const leave = it.kind === "leave";
  const Icon = leave ? CalendarDays : Wallet;
  return (
    <div data-testid={`mgr-req-${it.id}`} className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${leave ? "bg-sky-50 text-sky-600" : "bg-emerald-50 text-emerald-600"}`}><Icon className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-slate-900">{leave ? "طلب إجازة" : "طلب سلفة"} — {it.employee_name}</p>
          <p className="mt-0.5 text-xs text-slate-500">{[it.department, it.branch].filter(Boolean).join(" · ")}</p>
          <p className="mt-1.5 text-sm text-slate-700">
            {leave ? <span dir="ltr">{it.start_date} → {it.end_date} ({it.days} يوم)</span> : <><b>{it.amount}</b> ر.س{it.reason ? ` — ${it.reason}` : ""}</>}
          </p>
        </div>
      </div>
      {rejecting ? (
        <div className="mt-3 flex gap-2">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="سبب الرفض (اختياري)" data-testid={`mgr-reason-${it.id}`} />
          <Button variant="destructive" disabled={busy} onClick={() => run(() => api.mgrReject(it.kind, it.id, reason), "تم رفض الطلب وإبلاغ الموظف")} data-testid={`mgr-reject-confirm-${it.id}`}>رفض</Button>
          <Button variant="ghost" onClick={() => setRejecting(false)}>إلغاء</Button>
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <Button disabled={busy} className="flex-1 gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={() => run(() => api.mgrApprove(it.kind, it.id), "تمت موافقتك — أُرسل للاعتماد النهائي")} data-testid={`mgr-approve-${it.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} موافقة
          </Button>
          <Button variant="outline" disabled={busy} className="flex-1 gap-1.5 text-rose-600 hover:bg-rose-50" onClick={() => setRejecting(true)} data-testid={`mgr-reject-${it.id}`}><X className="h-4 w-4" /> رفض</Button>
        </div>
      )}
    </div>
  );
};

export const MgrApprovalsButton = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.mgrApprovals().then((d) => setItems(d.items || [])).catch(() => {}), []);
  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, [load]);
  if (!items.length && !open) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="mgr-approvals-btn" className="gap-1.5 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100">
        <Inbox className="h-4 w-4" /> طلبات قسمي <span className="rounded-full bg-amber-600 px-1.5 text-[11px] font-bold text-white" data-testid="mgr-approvals-count">{items.length}</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="mgr-approvals-dialog">
          <DialogHeader className="text-start">
            <DialogTitle>طلبات بانتظار موافقتك</DialogTitle>
            <DialogDescription>موافقتك ترسل الطلب لاعتماد الإدارة النهائي، والرفض يُبلغ الموظف مباشرة.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {items.map((it) => <ReqCard key={`${it.kind}-${it.id}`} it={it} onDone={load} />)}
            {!items.length && <p className="py-8 text-center text-sm text-slate-500" data-testid="mgr-approvals-empty">لا توجد طلبات معلقة 🎉</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
